import {CLOSE_ALL_MODALS, MODAL_CLOSE, MODAL_SHOW} from "@atg-shared/modals-types";
import {broadcastAction, frameAction} from "atg-store-addons";

import type {DepositModalProps} from "@atg-payment-shared/deposit-types";
import type {KambiModalData, DepositLimitModalData} from "@atg-shared/modals-types";

const show = (modalId: string) => () => ({
    type: MODAL_SHOW,
    payload: modalId,
});

// Why not adding "payload" to above function? Some components pass these actions
// directly on onClick handlers like `onClick={showModal}`, and that would make
// the whole event object being sent through our reducers, and might end up in the store
// Make modals that need extra data passed explicitly pass a payload to avoid this
const showWithExtraPayload =
    <T>(modalId: string) =>
    (payload: T) => ({
        type: MODAL_SHOW,
        payload: {...payload, modalId},
    });

const close = (modalId: string) => () => ({
    type: MODAL_CLOSE,
    payload: modalId,
});

// Login modal
export const showLoginModal = show("loginModal");
export const closeLoginModal = close("loginModal");

// Change password modal
export const showChangePasswordModal = show("changePasswordModal");
export const closeChangePasswordModal = close("changePasswordModal");

// Horse modals
export const showScratchedHorseWarningModal = show("betOnScratchedHorseModal");
export const closeScratchedHorseWarningModal = close("betOnScratchedHorseModal");

export const showStartlistOptionsModal = show("startlistOptionsModal");
export const closeStartlistOptionsModal = close("startlistOptionsModal");
export const showResultsOptionsModal = show("resultsOptionsModal");
export const closeResultsOptionsModal = close("resultsOptionsModal");

// Harry modals
export const showHarrySelectionModal = show("harrySelectionModal");
export const closeHarrySelectionModal = close("harrySelectionModal");

// Two factor
export const showTwoFactorConfirmationModal = show("twoFactorConfirmationModal");
export const closeTwoFactorConfirmationModal = close("twoFactorConfirmationModal");
export const showTwoFactorLoginConfirmationModal = show(
    "twoFactorLoginConfirmationModal",
);
export const closeTwoFactorLoginConfirmationModal = close(
    "twoFactorLoginConfirmationModal",
);
export const showTwoFactorActivationModal = show("twoFactorActivationModal");
export const closeTwoFactorActivationModal = close("twoFactorActivationModal");
export const showTwoFactorDeactivationModal = show("twoFactorDeactivationModal");
export const closeTwoFactorDeactivationModal = close("twoFactorDeactivationModal");

// User gambling summary screen
export const showUserGamblingSummaryModal = show("userGamblingSummaryModal");
export const closeUserGamblingSummaryModal = () =>
    broadcastAction(close("userGamblingSummaryModal")());

// Contact Information Update modal
export const showUpdateContactInformationModal = show("updateContactInformationModal");
export const closeUpdateContactInformationModal = close("updateContactInformationModal");

// Verify Contact Information modal
export const showVerifyContactInformationModal = show("verifyContactInformationModal");
export const closeVerifyContactInformationModal = close("verifyContactInformationModal");

// KYC modal
export const showKycQuestionnaireModal = show("kycQuestionnaireModal");
export const closeKycQuestionnaireModal = close("kycQuestionnaireModal");
export const showKycQuestionnaireFinishedModal = show("kycQuestionnaireFinishedModal");
export const closeKycQuestionnaireFinishedModal = close("kycQuestionnaireFinishedModal");

// RGS
export const showRgsAtgCheckModal = show("rgsAtgCheckModal");
export const closeRgsAtgCheckModal = close("rgsAtgCheckModal");

// Deposit in modal
export const showDepositModal = (payload?: DepositModalProps) =>
    frameAction(showWithExtraPayload("depositModal")(payload));
export const closeDepositModal = () => frameAction(close("depositModal")());

export const showWithdrawModal = () => frameAction(show("withdrawModal")());
export const closeWithdrawModal = () => frameAction(close("withdrawModal")());

export const showBig9ClearBetslipModal = show("big9ClearBetslipModal");
export const closeBig9ClearBetslipModal = close("big9ClearBetslipModal");

export const showOldBig9StatisticsModal = show("oldBig9StatisticsModal");
export const closeOldBig9StatisticsModal = close("oldBig9StatisticsModal");

export const showBig9StatisticsModal = show("big9StatisticsModal");
export const closeBig9StatisticsModal = close("big9StatisticsModal");

export const showGotoAccountSettingsModal = show("gotoAccountSettingsModal");
export const closeGotoAccountSettingsModal = close("gotoAccountSettingsModal");

export const showDepositLimitModal = showWithExtraPayload<
    DepositLimitModalData | undefined | void
>("depositLimitModal");
export const closeDepositLimitModal = close("depositLimitModal");

// Kambi modal
export const showKambiModal = showWithExtraPayload<KambiModalData>("kambiModal");
export const closeKambiModal = close("kambiModal");

// Session Reminder Modal
export const showSessionReminderModal = show("sessionReminderModal");
export const closeSessionReminderModal = close("sessionReminderModal");

// Image upload
export const closeImageUploadModal = close("imageUploadModal");

// App - Deposit Limit Reminder
export const showDepositLimitReminder = show("depositLimitReminder");
export const closeDepositLimitReminder = close("depositLimitReminder");

// Chat - Block message modal
export const showBlockChatMemberModal = show("blockChatMemberModal");
export const closeBlockChatMemberModal = close("blockChatMemberModal");

// Chat - Report message modal
export const showReportChatMessageModal = show("reportChatMessageModal");
export const closeReportChatMessageModal = close("reportChatMessageModal");

export const showReducedCouponStakeModal = show("reducedCouponStakeModal");
export const closeReducedCouponStakeModal = close("reducedCouponStakeModal");

// Casino modals
export const showCasinoPanicModal = show("casinoPanicModal");
export const closeCasinoPanicModal = close("casinoPanicModal");
export const showCasinoErrorModal = show("casinoErrorModal");
export const closeCasinoErrorModal = close("casinoErrorModal");
export const showCasinoLossLimitModal = show("casinoLossLimitModal");
export const closeCasinoLossLimitModal = close("casinoLossLimitModal");

// Sportbook modals
export const showSearchSportModal = show("searchSport");
export const closeSearchSportModal = close("searchSport");

// Account settings modals
export const showEditPushNotificationSettingsModal = show(
    "editPushNotificationSettingsModal",
);
export const closeEditPushNotificationSettingsModal = close(
    "editPushNotificationSettingsModal",
);

export const showEditMobileNumberModal = show("editMobileNumberModal");
export const closeEditMobileNumberModal = close("editMobileNumberModal");
export const showEditEmailModal = show("editEmailModal");
export const closeEditEmailModal = close("editEmailModal");

// Misc modals
export const showLoginTimeReachedLogoutModal = show("loginTimeReachedLogoutModal");
export const closeLoginTimeReachedLogoutModal = close("loginTimeReachedLogoutModal");
export const showFifteenMinutesLeftModal = show("fifteenMinutesLeftModal");
export const closeFifteenMinutesLeftModal = close("fifteenMinutesLeftModal");

export const showVmModal = show("VmModal");
export const closeVmModal = close("VmModal");
export const showAlreadyRegisteredInVmModal = show("alreadyRegisteredInVmModal");
export const closeAlreadyRegisteredInVmModal = close("alreadyRegisteredInVmModal");
export const showVmErrorModal = show("VmErrorModal");
export const closeVmErrorModal = close("VmErrorModal");

export const showStakeGameBetCostModal = show("stakeGameBetCostModal");
export const closeStakeGameBetCostModal = close("stakeGameBetCostModal");

export const showQuickPlayStakeGameBetCostModal = show("quickPlayStakeGameBetCostModal");
export const closeQuickPlayStakeGameBetCostModal = close(
    "quickPlayStakeGameBetCostModal",
);

// Confrim bet modal
export const showConfirmBetModal = show("confirmBetModal");
export const closeConfirmBetModal = close("confirmBetModal");

/*
 *  Unused JS actions ends
 */

// Change password
/**
 * @deprecated These actions are not used anymore, and will be removed in the next major release. Prevously only used in JS file but not in TS export from atg-modals.
 */
export const showChangeTempPasswordModal = show("changeTempPasswordModal");
/**
 * @deprecated These actions are not used anymore, and will be removed in the next major release. Prevously only used in JS file but not in TS export from atg-modals.
 */
export const closeChangeTempPasswordModal = close("changeTempPasswordModal");
/**
 * @deprecated These actions are not used anymore, and will be removed in the next major release. Prevously only used in JS file but not in TS export from atg-modals.
 */
export const closeAllModals = () => broadcastAction({type: CLOSE_ALL_MODALS});
/**
 * @deprecated These actions are not used anymore, and will be removed in the next major release. Prevously only used in JS file but not in TS export from atg-modals.
 */
export const showUserAgreementModal = show("userAgreementModal");
